import { energyImages } from '@/assets/images/energy';
import heroImage02 from '@/assets/images/hero/hvac-engineering.webp';
import heroImage01 from '@/assets/images/hero/mechanical-engineering.webp';
import refrigerationHeroImage from '@/assets/images/hero/refrigeration-system.webp';
import { mechanicalHvacImages } from '@/assets/images/mechanical-hvac';
import { refrigerationSystemImages } from '@/assets/images/refrigeration-system';
import type {
  CatalogCard,
  CatalogCategory,
  CatalogFilterGroup,
  CatalogGalleryImage,
  CatalogMetric,
  CatalogTab,
} from '@/features/catalog/types';
import type { catalogCategorySlugs, CatalogCategorySlug } from '@/routes/routeRegistry';

interface CatalogCardSeed {
  detailDescription: string;
  filters: Record<string, string[]>;
  gallery: CatalogGalleryImage[];
  highlights: string[];
  id: string;
  imageAlt: string;
  imageSrc: string;
  metrics: CatalogMetric[];
  model: string;
  summary: string;
  tabId: string;
  tags: string[];
  title: string;
}

const createGallery = (
  title: string,
  ...items: Array<{ src: string; alt?: string }>
): CatalogGalleryImage[] => {
  return items.map((item, index) => {
    return {
      alt: item.alt ?? `${title} 갤러리 이미지 ${String(index + 1)}`,
      src: item.src,
    };
  });
};

const createCard = (categoryId: CatalogCategorySlug, seed: CatalogCardSeed): CatalogCard => {
  return {
    ...seed,
    categoryId,
    slug: seed.id,
  };
};

const createAliasedCard = (
  source: CatalogCard,
  categoryId: CatalogCategorySlug,
  overrides: Partial<Omit<CatalogCard, 'categoryId' | 'slug'>> & { id: string },
): CatalogCard => {
  return {
    ...source,
    ...overrides,
    categoryId,
    slug: overrides.id,
  };
};

const allTab: CatalogTab = {
  id: 'all',
  label: '전체',
};

const energyTabs: CatalogTab[] = [
  allTab,
  { id: 'gutner-unit-cooler', label: '군트너 유니트 쿨러' },
  { id: 'haier-carrier', label: '하이얼캐리어' },
  { id: 'built-in-frozen-showcase', label: '내치형 냉동 쇼케이스' },
  { id: 'showcase-wine-cellar', label: '프리미엄 와인셀러' },
  { id: 'open-showcase-door', label: '오픈 쇼케이스 도어' },
  { id: 'motor-direct-control-system', label: '모터 직결제어 시스템' },
  { id: 'ahu-refill-filter', label: '공조기 리필형 필터' },
];

const mechanicalTabs: CatalogTab[] = [
  allTab,
  { id: 'hvac-facility', label: '공조설비' },
  { id: 'heating-cooling-facility', label: '냉난방설비' },
  { id: 'plumbing-facility', label: '위생설비' },
  { id: 'automatic-control', label: '자동제어' },
  { id: 'operating-consulting', label: '운영 컨설팅' },
  { id: 'turbo-chiller', label: '터보냉동기' },
  { id: 'cooling-tower', label: '냉각탑' },
  { id: 'boiler', label: '보일러' },
];

const refrigerationTabs: CatalogTab[] = [
  allTab,
  { id: 'distribution-system', label: '유통' },
  { id: 'agricultural-fisheries-cold-storage-system', label: '농수산 저온창고' },
  { id: 'ice-making-system', label: '제빙' },
  { id: 'technical-design-specialized-construction', label: '기술설계 전문시공' },
  { id: 'maintenance-service', label: '유지보수' },
];

const energyFilterGroups: CatalogFilterGroup[] = [
  {
    id: 'application',
    label: '적용 분야',
    options: [
      { id: 'retail', label: '리테일 매장' },
      { id: 'logistics', label: '물류센터' },
      { id: 'cold-storage', label: '저온창고' },
    ],
  },
  {
    id: 'equipment',
    label: '구성 장비',
    options: [
      { id: 'refrigerant-system', label: '냉매시스템' },
      { id: 'cooler', label: '쿨러' },
      { id: 'control', label: '제어' },
      { id: 'filter', label: '필터' },
      { id: 'door', label: '도어' },
    ],
  },
  {
    id: 'priority',
    label: '운영 포인트',
    options: [
      { id: 'energy-saving', label: '에너지 절감' },
      { id: 'eco-friendly', label: '친환경' },
      { id: 'maintenance', label: '유지관리성' },
    ],
  },
];

const mechanicalFilterGroups: CatalogFilterGroup[] = [
  {
    id: 'discipline',
    label: '설비 영역',
    options: [
      { id: 'consulting', label: '운영 컨설팅' },
      { id: 'heating-cooling', label: '냉난방 설비' },
      { id: 'air-conditioning', label: '공조설비' },
      { id: 'plumbing', label: '위생설비' },
      { id: 'control', label: '자동제어' },
      { id: 'turbo-chiller', label: '터보냉동기' },
      { id: 'cooling-tower', label: '냉각탑' },
      { id: 'boiler', label: '보일러' },
    ],
  },
  {
    id: 'site',
    label: '적용 공간',
    options: [
      { id: 'commercial', label: '상업시설' },
      { id: 'office', label: '업무시설' },
      { id: 'logistics', label: '물류시설' },
    ],
  },
  {
    id: 'delivery',
    label: '수행 범위',
    options: [
      { id: 'design', label: '설계' },
      { id: 'construction', label: '시공' },
      { id: 'commissioning', label: '운전 최적화' },
    ],
  },
];

const refrigerationFilterGroups: CatalogFilterGroup[] = [
  {
    id: 'system',
    label: '시스템 유형',
    options: [
      { id: 'engineering', label: '전문 시공' },
      { id: 'cold-chain', label: '콜드체인' },
      { id: 'maintenance', label: '유지보수' },
      { id: 'showcase', label: '쇼케이스' },
      { id: 'cellar', label: '와인셀러' },
    ],
  },
  {
    id: 'product',
    label: '보관 품목',
    options: [
      { id: 'seafood', label: '수산물' },
      { id: 'frozen-food', label: '냉동식품' },
      { id: 'beverage', label: '음료·주류' },
    ],
  },
  {
    id: 'operation',
    label: '운영 포인트',
    options: [
      { id: 'temperature', label: '저온 유지' },
      { id: 'layout', label: '동선 효율' },
      { id: 'service', label: '유지관리' },
    ],
  },
];

const energyCards: CatalogCard[] = [
  createCard('energy-solution', {
    id: 'co2-natural-refrigerant-system-retail',
    tabId: 'co2-natural-refrigerant-system',
    title: '자연냉매(CO₂) 냉동냉장\n멀티 시스템',
    model: 'QCMT100 ACO 6 EC MX· QCLT167 ACO 12 EC MX',
    summary:
      '자연냉매 CO₂를 적용해 소형 유통매장부터 대형마트와\n저온 물류창고까지 대응하는 냉동냉장 멀티 시스템',
    detailDescription:
      '자연냉매 CO₂를 기반으로 탄소중립 대응, 에너지 절감, 저소음 운전, 간편 정비를 함께 고려한 냉동냉장 멀티 시스템입니다. 소형 유통매장부터 대형마트, 저온 물류창고까지 폭넓게 대응할 수 있도록 라인업과 운전 범위를 구성했습니다.',
    imageSrc: energyImages.co2.customGallery.preview01,
    imageAlt: '자연냉매(CO₂) 냉동냉장 멀티 시스템 이미지',
    tags: ['#친환경', '#자연냉매', '#에너지절감'],
    highlights: [
      '탄소중립·ESG 대응 친환경 시스템',
      '약 30% 이상 에너지 절감 고효율 시스템',
      '저소음·간편정비 기반 안전 시스템',
      '1kW부터 2.2MW까지 대응 가능한 라인업',
    ],
    metrics: [
      { label: '냉매 전략', value: '자연냉매 CO₂' },
      { label: '에너지 효과', value: '약 30% 절감 가능' },
      { label: '대응 범위', value: '1kW-2.2MW' },
      { label: '라인업', value: '소·중·대형 유니트' },
    ],
    gallery: createGallery(
      '자연냉매(CO₂) 냉동냉장 멀티 시스템',
      {
        src: energyImages.co2.customGallery.preview01,
        alt: '자연냉매(CO₂) 냉동냉장 멀티 시스템 메인 미리보기 이미지',
      },
      {
        src: energyImages.co2.customGallery.detailStage02,
        alt: '자연냉매(CO₂) 냉동냉장 멀티 시스템 상세 미리보기 이미지',
      },
    ),
    filters: {
      application: ['retail', 'logistics', 'cold-storage'],
      equipment: ['refrigerant-system', 'control'],
      priority: ['energy-saving', 'eco-friendly', 'maintenance'],
    },
  }),
  createCard('energy-solution', {
    id: 'gutner-unit-cooler-high-flow',
    tabId: 'gutner-unit-cooler',
    title: '군트너',
    model: 'Cubic COMPACT',
    summary: '중형 저온 저장고 및 마트 환경에 적합한 고효율 큐빅형 유니트쿨러',
    detailDescription:
      '90년 전통의 유럽 에어쿨러 전문기업 군트너의 제품으로, 위생성과 에너지 효율을 함께 고려한 냉동·냉장 시스템용 에어쿨러입니다.',
    imageSrc: energyImages.guntner.customGallery.preview01,
    imageAlt: '군트너 유니트 쿨러 이미지',
    tags: ['#군트너', '#유니트쿨러', '#CO₂대응'],
    highlights: [
      '전세계 유일 HACCP 인증제품',
      '유럽 기준 설계 기반 안정적 운영',
      '친환경 자연냉매 시스템 적용 가능',
      '동일 용량 대비 냉각 성능 향상',
    ],
    metrics: [
      { label: 'CO₂ 대응', value: '친환경 자연냉매 시스템' },
      { label: 'HACCP', value: '전세계 유일 인증제품' },
      { label: 'AlMg', value: '부식방지 합금 케이스' },
      { label: 'EC FAN', value: '부하 연동 기반 전력 소비 절감' },
    ],
    gallery: createGallery(
      '군트너 유니트 쿨러',
      {
        src: energyImages.guntner.customGallery.preview01,
        alt: '군트너 유니트 쿨러 메인 미리보기 이미지',
      },
      {
        src: energyImages.guntner.customGallery.preview08,
        alt: '군트너 유니트 쿨러 보조 미리보기 이미지',
      },
      {
        src: energyImages.guntner.customGallery.preview09,
        alt: '군트너 유니트 쿨러 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      application: ['cold-storage', 'logistics'],
      equipment: ['cooler'],
      priority: ['maintenance', 'energy-saving'],
    },
  }),
  createCard('energy-solution', {
    id: 'ahu-refill-filter-modular',
    tabId: 'ahu-refill-filter',
    title: '공조기 리필형 필터',
    model: '리필형 프리필터 · 미듐필터',
    summary:
      '기존 공조기 프레임을 재사용하고 필터만 간편 교체하는 특허형 구조로 유지관리비와 에너지 비용을 함께 줄이는 공조기 필터 솔루션입니다.',
    detailDescription:
      '기계설비 브로셔 기준 공조기 리필형 필터는 재사용 특허 프레임에 리필형 미듐필터와 프리필터를 결합해 적용하는 구조입니다. 최초 1회 기존 공조기에 프레임을 삽입한 뒤에는 필터만 교체하는 방식으로 운용해 프레임 폐기와 교체 시간을 줄이고, 다중망 필터 적용과 난연 소재 구성을 통해 공기질 확보와 에너지 비용 절감, 유지관리 안전성을 함께 고려합니다.',
    imageSrc: energyImages.ahuFilter.customGallery.preview01,
    imageAlt: '공조기 리필형 필터 이미지',
    tags: ['#공조기', '#리필형필터', '#특허제품'],
    highlights: [
      '프레임 하나에 두 개 필터를 적용한 일체형 리필 구조',
      '최초 설치 후에는 필터만 교체, 프레임은 5~10년 재사용',
      '다풍량 필터 구조와 공기 누수 저감 설계로 에너지 비용 절감',
      '난연 소재 적용, 폐기물 저감, 공급 실적까지 갖춘 특허 제품',
    ],
    metrics: [
      { label: '적용 설비', value: '공조기·AHU' },
      { label: '교체 방식', value: '필터만 교체' },
      { label: '프레임 운용', value: '약 5~10년 재사용' },
      { label: '운영 효과', value: '유지관리비·폐기물 절감' },
    ],
    gallery: createGallery(
      '공조기 리필형 필터',
      {
        src: energyImages.ahuFilter.customGallery.preview01,
        alt: '공조기 리필형 필터 프리뷰 이미지 1',
      },
      {
        src: energyImages.ahuFilter.customGallery.preview04,
        alt: '공조기 리필형 필터 프리뷰 이미지 2',
      },
      {
        src: energyImages.ahuFilter.customGallery.preview05,
        alt: '공조기 리필형 필터 프리뷰 이미지 3',
      },
    ),
    filters: {
      application: ['retail'],
      equipment: ['filter'],
      priority: ['energy-saving', 'maintenance'],
    },
  }),
  createCard('energy-solution', {
    id: 'motor-direct-control-system-smart-fan',
    tabId: 'motor-direct-control-system',
    title: '모터 직결제어시스템',
    model: '저전력 직결팬 · EASY SHAFT',
    summary:
      '벨트와 풀리 구조를 줄인 저전력 직결 기술로 공조 송풍기의 전력 사용량과 유지관리 부담을 함께 낮추는 특허형 직결 제어 솔루션입니다.',
    detailDescription:
      '기계설비 브로셔 기준 저전력 직결팬은 벨트와 체인, 구리스 주입 요소를 줄인 일체형 직결 구조를 통해 30~40% 전력 절감 효과를 제안합니다. 무벨트 운용으로 유지보수가 쉬워지고 관리자의 안전사고 예방에 유리하며, 고효율 모터와 인버터 설치 시 관련 지원금 대상까지 고려하는 구성을 특징으로 합니다. 리뉴얼 가이드 18페이지의 제안 자료는 Easy Shaft 구조와 에너지 절감, 유지보수 비용 감소, 안전사고 예방 관점의 보조 설명 자료로 함께 활용할 수 있습니다.',
    imageSrc: energyImages.motorDirectControl.customGallery.preview01,
    imageAlt: '모터 직결제어시스템 이미지',
    tags: ['#직결제어', '#저전력직결팬', '#전력절감'],
    highlights: [
      '기존 V-Belt Fan을 대체하는 저전력 직결 구동 시스템',
      '벨트·풀리 없는 구조로 유지관리 부담 절감',
      '전력 손실을 줄여 30~40% 에너지 절감 효과',
      '안전성과 운영 효율을 높인 특허 기반 직결 설계',
    ],
    metrics: [
      { label: '적용 설비', value: '공조 송풍기·냉각탑 팬' },
      { label: '구동 방식', value: '직결형 일체 구조' },
      { label: '전력 효과', value: '약 30~40% 절감' },
      { label: '운영 포인트', value: '유지관리·안전성 개선' },
    ],
    gallery: createGallery(
      '모터 직결제어시스템',
      {
        src: energyImages.motorDirectControl.customGallery.preview01,
        alt: '모터 직결제어시스템 프리뷰 이미지 1',
      },
      {
        src: energyImages.motorDirectControl.customGallery.preview04,
        alt: '모터 직결제어시스템 프리뷰 이미지 2',
      },
      {
        src: energyImages.motorDirectControl.customGallery.preview05,
        alt: '모터 직결제어시스템 프리뷰 이미지 3',
      },
    ),
    filters: {
      application: ['retail', 'logistics', 'cold-storage'],
      equipment: ['control'],
      priority: ['energy-saving', 'maintenance'],
    },
  }),
  createCard('energy-solution', {
    id: 'open-showcase-door-retrofit',
    tabId: 'open-showcase-door',
    title: '오픈 쇼케이스 도어',
    model: 'SLIDING DOOR · SWING DOOR',
    summary:
      '개방형 쇼케이스의 냉기 손실을 줄이고 매장 운영 효율과\n시공 편의성을 함께 고려한 맞춤형 도어 솔루션',
    detailDescription:
      '기존 오픈형 쇼케이스에 도어를 적용해 냉기 손실을 줄이고, 매장 동선과 설치 환경에 따라 SLIDING DOOR와 SWING DOOR 중 적합한 방식을 선택할 수 있는 도어 시스템입니다.',
    imageSrc: energyImages.openShowcaseDoor.customGallery.preview01,
    imageAlt: '오픈 쇼케이스 도어 이미지',
    tags: ['#쇼케이스도어', '#리테일개선', '#전기료절감'],
    highlights: [
      '냉기 손실 저감을 고려한 도어 적용 구조',
      '설치 환경에 맞춘 미닫이·여닫이 선택 구성',
      '결로 대응과 시인성 확보를 고려한 안정 운전 설계',
      '기존 매장에 빠르게 적용 가능한 현장 시공 지원',
    ],
    metrics: [
      { label: '냉기 절감', value: '40% 전기료 절감 가능' },
      { label: '정부지원', value: '최대 60% 혜택 가능' },
      { label: '투자 회수', value: '9-13개월' },
      { label: '시공 대응', value: '2-3주 · 6시간' },
    ],
    gallery: createGallery(
      '오픈 쇼케이스 도어',
      {
        src: energyImages.openShowcaseDoor.customGallery.preview01,
        alt: '오픈 쇼케이스 도어 메인 미리보기 이미지',
      },
      {
        src: energyImages.openShowcaseDoor.customGallery.detailStage02,
        alt: '오픈 쇼케이스 도어 보조 미리보기 이미지',
      },
      {
        src: energyImages.openShowcaseDoor.customGallery.valueAfter07,
        alt: '오픈 쇼케이스 도어 적용 미리보기 이미지',
      },
    ),
    filters: {
      application: ['retail'],
      equipment: ['door'],
      priority: ['energy-saving', 'maintenance'],
    },
  }),
];

const mechanicalCards: CatalogCard[] = [
  createCard('mechanical-hvac', {
    id: 'operating-consulting-diagnostic',
    tabId: 'operating-consulting',
    title: '운영 컨설팅',
    model: '현장진단 · 운영관리 · 효율개선',
    summary:
      '운영상태 점검과 현장 조건 분석을 통해 효율 개선 방향과 운\n영 최적화 방안을 제안하는 운영 컨설팅 서비스',
    detailDescription:
      '운영상태 점검과 현장 조건 분석을 기반으로 효율 개선 방향과 운영 최적화 방안을 제안하는 운영 컨설팅 서비스입니다. 반복 대응이 아니라 진단, 분석, 제안, 개선 검토를 하나의 흐름으로 연결해 실질적인 운영 방향을 정리하는 방식으로 구성했습니다.',
    imageSrc: mechanicalHvacImages.overview.customGallery.preview01,
    imageAlt: '운영 컨설팅 이미지',
    tags: ['#운영컨설팅', '#현장진단', '#효율개선'],
    highlights: [
      '운영상태 점검 기반의 진단',
      '현장 조건 반영형 분석',
      '최적 설계 제안 중심 컨설팅',
      '효율 개선·비용 절감 솔루션 제안',
    ],
    metrics: [
      { label: '진단', value: '운영상태 점검' },
      { label: '분석', value: '현장 조건 검토' },
      { label: '제안', value: '최적 솔루션 제안' },
      { label: '개선', value: '효율·비용 개선 검토' },
    ],
    gallery: createGallery(
      '운영 컨설팅',
      { src: mechanicalHvacImages.overview.customGallery.preview01 },
      { src: mechanicalHvacImages.overview.customGallery.preview08 },
      { src: mechanicalHvacImages.overview.customGallery.valueAfter07 },
    ),
    filters: {
      discipline: ['consulting'],
      site: ['commercial', 'office', 'logistics'],
      delivery: ['design', 'commissioning'],
    },
  }),
  createCard('mechanical-hvac', {
    id: 'heating-cooling-facility-modular',
    tabId: 'heating-cooling-facility',
    title: '냉난방 설비',
    model: '성능 점검 · 시공 점검 · 유지보수',
    summary: '냉난방 장비의 성능 진단과 정밀 점검을 통해\n최적의 설계·시공·유지보수를 제공합니다.',
    detailDescription:
      '장비 성능 점검과 T.A.B 측정을 기반으로 정확한 설계 및 시공 점검, 장비 교체와 유지보수 공사까지 대응하는 냉난방설비 서비스입니다.',
    imageSrc: mechanicalHvacImages.heatingCooling.customGallery.preview01,
    imageAlt: '냉난방 설비 이미지',
    tags: ['#냉난방설비', '#유지보수'],
    highlights: [
      '장비 성능 효율 진단',
      '정확한 설계 및 시공 점검',
      '장비 교체 공사 대응',
      '유지보수 공사 연계',
    ],
    metrics: [
      { label: '진단', value: '성능 점검' },
      { label: '측정', value: 'T.A.B' },
      { label: '검토', value: '시공 점검' },
      { label: '연계', value: '유지보수' },
    ],
    gallery: createGallery(
      '냉난방 설비',
      {
        src: mechanicalHvacImages.heatingCooling.customGallery.preview01,
        alt: '냉난방 설비 메인 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.heatingCooling.customGallery.detailStage02,
        alt: '냉난방 설비 보조 미리보기 이미지',
      },
    ),
    filters: {
      discipline: ['heating-cooling'],
      site: ['commercial', 'office'],
      delivery: ['design', 'construction'],
    },
  }),
  createCard('mechanical-hvac', {
    id: 'hvac-facility-air-handler',
    tabId: 'hvac-facility',
    title: '공조설비',
    model: '공조설비 · 송풍기',
    summary: '공조기 시공과 장비 유지보수를 기반으로\n에너지 절감과 쾌적한 실내환경을 구현합니다.',
    detailDescription:
      '브로셔 기준 공조설비는 공조기 시공 및 정비 유지보수 공사를 중심으로, 용도별 최적 설계와 정보 제어를 통해 에너지 절감과 쾌적한 실내환경을 제공하는 구조입니다. 함께 구성되는 송풍기는 급·배기 장비 설치, 베어링 교체, 샤프트 점검, 오버홀 등 유지보수 범위를 포함하며, 맞춤 제작과 풍량·정밀조정 시공으로 소음과 진동을 줄여 장비 신뢰성을 높이는 방향으로 정리했습니다.',
    imageSrc: mechanicalHvacImages.hvac.customGallery.preview01,
    imageAlt: '공조설비 이미지',
    tags: ['#공조설비', '#송풍기', '#실내환경제어'],
    highlights: [
      '공조기 시공 및 정비 유지보수 공사를 중심으로 운용하는 공조설비 항목입니다.',
      '용도별 최적 설계와 정보 제어를 통해 에너지 절감과 쾌적한 실내환경 조성을 함께 목표로 합니다.',
      '송풍기는 급·배기 장비 설치, 베어링, 샤프트, 오버홀 등 유지보수 범위를 포함합니다.',
      '맞춤 제작과 풍량·정밀조정 시공으로 소음과 진동을 최소화하고 장비 신뢰성을 높입니다.',
    ],
    metrics: [
      { label: '핵심 장비', value: '공조기·송풍기' },
      { label: '수행 범위', value: '시공·정비·유지보수' },
      { label: '운영 목표', value: '에너지 절감·쾌적성' },
      { label: '관리 포인트', value: '풍량·소음·진동 제어' },
    ],
    gallery: createGallery(
      '공조설비',
      {
        src: mechanicalHvacImages.hvac.customGallery.preview01,
        alt: '공조설비 메인 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.hvac.customGallery.preview08,
        alt: '공조설비 보조 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.hvac.customGallery.preview09,
        alt: '공조설비 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      discipline: ['air-conditioning'],
      site: ['office', 'commercial'],
      delivery: ['design', 'construction', 'commissioning'],
    },
  }),
  createCard('mechanical-hvac', {
    id: 'plumbing-facility-hygiene',
    tabId: 'plumbing-facility',
    title: '위생 설비',
    model: '위생설비 · 급배수시설 · 기계실 조성',
    summary:
      '급·배수시설 시공과 장비 교체, 유지보수 공사를 통해\n안전한 수질 환경을 구축하는 위생설비 서비스',
    detailDescription:
      '급·배수시설 시공과 장비 교체, 유지보수 공사를 통해 안전한 수질 환경을 구축하는 위생설비 서비스입니다. 급수 설비, 배관 시공, 기계실 조성, 유지보수까지 연결해 부분 대응이 아닌 체계적인 시공과 운영 관점으로 정리했습니다.',
    imageSrc: mechanicalHvacImages.plumbing.customGallery.preview01,
    imageAlt: '위생설비 이미지',
    tags: ['#급배수설비', '#배관시공', '#유지보수'],
    highlights: [
      '급·배수시설 시공 대응',
      '다양한 배관 공사 수행',
      '기계실 조성 공사 연계',
      '장비 교체 및 유지보수 지원',
    ],
    metrics: [
      { label: '급수 설비', value: '급수 시스템 대응' },
      { label: '배관 시공', value: '다양한 배관 공사' },
      { label: '기계실 조성', value: '설비 환경 구축' },
      { label: '유지보수', value: '교체·보수 연계' },
    ],
    gallery: createGallery(
      '위생 설비',
      { src: mechanicalHvacImages.plumbing.customGallery.preview01 },
      { src: mechanicalHvacImages.plumbing.customGallery.detailStage02 },
      { src: mechanicalHvacImages.plumbing.customGallery.valueAfter07 },
    ),
    filters: {
      discipline: ['plumbing'],
      site: ['commercial', 'office'],
      delivery: ['design', 'construction'],
    },
  }),
  createCard('mechanical-hvac', {
    id: 'automatic-control-bms',
    tabId: 'automatic-control',
    title: '자동제어',
    model: '자동제어 공사 · 인버터 · 제어판넬',
    summary:
      '자동제어 인버터 설치·운영 관리,\n정밀 제어 시스템 구축 기반의 안정적·효율적 운전 환경 제공',
    detailDescription:
      '자동제어 배관 시공부터 패널 제작 시공, 프로그램 설치 운영, 인버터 시공 및 운영 관리까지 대응하는 자동제어 공사 서비스입니다. 배관, 패널, 프로그램, 인버터를 분리하지 않고 운영까지 연결해 안정적이고 효율적인 운전 환경을 구축하는 방향으로 정리했습니다.',
    imageSrc: mechanicalHvacImages.automaticControl.customGallery.preview01,
    imageAlt: '자동제어 이미지',
    tags: ['#자동제어', '#인버터', '#제어판넬'],
    highlights: [
      '자동제어 배관 시공',
      '패널 제작 시공',
      '프로그램 설치 운영',
      '인버터 시공 및 운영 관리',
    ],
    metrics: [
      { label: '배관 시공', value: '제어 배관 시공' },
      { label: '패널 제작', value: '제어 패널 제작 시공' },
      { label: '프로그램 운영', value: '프로그램 설치 운영 관리' },
      { label: '인버터', value: '인버터 제작·시공' },
    ],
    gallery: createGallery(
      '자동제어',
      { src: mechanicalHvacImages.automaticControl.customGallery.preview01 },
      { src: mechanicalHvacImages.automaticControl.customGallery.detailStage02 },
      { src: mechanicalHvacImages.automaticControl.customGallery.valueAfter07 },
    ),
    filters: {
      discipline: ['control'],
      site: ['commercial', 'logistics', 'office'],
      delivery: ['commissioning', 'construction'],
    },
  }),
  createCard('mechanical-hvac', {
    id: 'turbo-chiller-facility-maintenance',
    tabId: 'turbo-chiller',
    title: '터보 냉동기',
    model: '맞춤 관리 · 성능 유지 · 수명 연장',
    summary:
      '장비 상태별 맞춤 관리로 최적의 성능을 유지하며,\n고효율 냉동기 교체 및 시공·유지보수를 제공합니다.',
    detailDescription:
      '장비 상태와 운전 조건을 함께 검토해 고효율 냉동기 교체, 냉동기 시공, 장비류 배관 구성, 유지보수까지 연결하는 터보 냉동기 설비 서비스입니다.',
    imageSrc: mechanicalHvacImages.turboChiller.customGallery.preview01,
    imageAlt: '터보 냉동기 이미지',
    tags: ['#고효율', '#에너지절감', '#유지보수'],
    highlights: ['고효율 냉동기 교체', '냉동기 시공 관리', '장비류 배관 구성', '유지보수 대응'],
    metrics: [
      { label: '교체', value: '고효율 냉동기' },
      { label: '시공', value: '냉동기 시공' },
      { label: '배관', value: '장비류 배관' },
      { label: '관리', value: '예방 관리' },
    ],
    gallery: createGallery(
      '터보 냉동기',
      {
        src: mechanicalHvacImages.turboChiller.customGallery.preview01,
        alt: '터보 냉동기 메인 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.turboChiller.customGallery.preview08,
        alt: '터보 냉동기 보조 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.turboChiller.customGallery.preview09,
        alt: '터보 냉동기 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      discipline: ['turbo-chiller'],
      site: ['commercial', 'office'],
      delivery: ['construction', 'commissioning'],
    },
  }),
  createCard('mechanical-hvac', {
    id: 'cooling-tower-facility-maintenance',
    tabId: 'cooling-tower',
    title: '냉각탑',
    model: '장비 교체 · 시공 점검 · 유지보수',
    summary:
      '부하별 최적 냉각 효율 구현과 체계적인 운전관리로\n냉각탑 교체 및 시공·유지보수를 제공합니다.',
    detailDescription:
      '냉각 부하와 운전 환경을 함께 확인하고, 냉각탑 교체 시공, 냉각수 계통 흐름 검토, 유지보수 관리를 연결하는 냉각탑 설비 서비스입니다.',
    imageSrc: mechanicalHvacImages.coolingTower.customGallery.preview01,
    imageAlt: '냉각탑 이미지',
    tags: ['#냉각효율', '#안전성', '#유지보수'],
    highlights: ['부하별 냉각 효율 관리', '냉각탑 교체 시공', '계통도 기반 검토', '유지보수 관리'],
    metrics: [
      { label: '교체', value: '냉각탑 교체' },
      { label: '시공', value: '냉각탑 시공' },
      { label: '계통', value: '설비 계통 흐름' },
      { label: '관리', value: '유지보수 관리' },
    ],
    gallery: createGallery(
      '냉각탑',
      {
        src: mechanicalHvacImages.coolingTower.customGallery.preview01,
        alt: '냉각탑 메인 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.coolingTower.customGallery.preview08,
        alt: '냉각탑 보조 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.coolingTower.customGallery.preview09,
        alt: '냉각탑 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      discipline: ['cooling-tower'],
      site: ['commercial', 'office'],
      delivery: ['construction', 'commissioning'],
    },
  }),
  createCard('mechanical-hvac', {
    id: 'boiler-facility-maintenance',
    tabId: 'boiler',
    title: '보일러',
    model: '교체 시공 · 비용 절감 · 유지보수',
    summary: '비용을 절감하는 효율적인 관리로\n보일러 교체 및 시공·유지보수를 제공합니다.',
    detailDescription:
      '안전기준과 현장 조건을 함께 검토해 보일러 교체 시공, 배관 및 펌프 관리, 유지보수까지 연결하는 보일러 설비 서비스입니다.',
    imageSrc: mechanicalHvacImages.boiler.customGallery.preview01,
    imageAlt: '보일러 이미지',
    tags: ['#안전기준', '#최적용량', '#유지보수'],
    highlights: ['최적 용량 검토', '보일러 교체 시공', '배관 및 펌프 관리', '유지보수 관리'],
    metrics: [
      { label: '교체', value: '보일러 교체' },
      { label: '시공', value: '현장 시공' },
      { label: '배관', value: '배관·펌프 관리' },
      { label: '안전', value: '안전기준' },
    ],
    gallery: createGallery(
      '보일러',
      {
        src: mechanicalHvacImages.boiler.customGallery.preview01,
        alt: '보일러 메인 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.boiler.customGallery.preview08,
        alt: '보일러 보조 미리보기 이미지',
      },
      {
        src: mechanicalHvacImages.boiler.customGallery.preview09,
        alt: '보일러 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      discipline: ['boiler'],
      site: ['commercial', 'office'],
      delivery: ['construction', 'commissioning'],
    },
  }),
];

const refrigerationCards: CatalogCard[] = [
  createCard('refrigeration-system', {
    id: 'technical-design-specialized-construction-retail',
    tabId: 'technical-design-specialized-construction',
    title: '기술설계 전문 시공',
    model: 'Technical Engineering Design',
    summary:
      '다양한 산업 환경에서 요구되는 냉동·냉장 및 공조 설비를 에너지 효율과 운영 안정성을 고려하여 최적화된 구조로 설계',
    detailDescription:
      '냉동·냉장, 제빙, 저온창고 및 공조·냉난방 설비 전반에 대해 현장 조건과 운영 목적을 반영한 맞춤형 기술설계를 제공합니다.',
    imageSrc: refrigerationSystemImages.technicalDesign.customGallery.preview01,
    imageAlt: '기술설계 전문시공 이미지',
    tags: ['#통합설계', '#전문시공', '#에너지 효율'],
    highlights: [
      '현장 조건 및 운영 목적 기반 설계',
      '에너지 절감 구조 적용',
      '냉동·공조·자동제어 연계 설계',
      '소형부터 대형 산업시설까지 설계 가능',
    ],
    metrics: [
      { label: '통합설계', value: '냉동·공조·자동제어 연계 설계' },
      { label: '20-30%', value: '에너지 효율 개선 구조' },
      { label: '맞춤설계', value: '현장 조건 기반 시스템' },
      { label: '전문시공', value: '설계·시공·관리 일관 대응' },
    ],
    gallery: createGallery(
      '기술설계 전문시공',
      {
        src: refrigerationSystemImages.technicalDesign.customGallery.preview01,
        alt: '기술설계 전문시공 메인 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.technicalDesign.customGallery.preview08,
        alt: '기술설계 전문시공 보조 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.technicalDesign.customGallery.preview09,
        alt: '기술설계 전문시공 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      system: ['engineering'],
      product: ['frozen-food', 'seafood'],
      operation: ['layout', 'temperature', 'service'],
    },
  }),
  createCard('refrigeration-system', {
    id: 'maintenance-service-monitoring',
    tabId: 'maintenance-service',
    title: '유지보수 서비스',
    model: '원스톱 대응 · 전국 서비스 네트워크',
    summary:
      '고객 전담 서비스 조직과 전국 서비스 네트워크를 기반으로 장애와 VOC를 원스톱으로 대응하는 냉동·냉장 설비 유지보수 서비스입니다.',
    detailDescription:
      '카탈로그 17페이지 기준 유지보수 서비스는 냉동·냉장 시스템 유지보수를 위한 고객 전담 서비스 조직과 전국 서비스 네트워크를 핵심으로 소개합니다. 장애와 VOC를 one stop으로 처리하고, 접수 후 3시간 내 전문기사 현장 출동, 장비 설치 후 2년 무상점검 서비스를 제공하는 구조입니다. 리뉴얼 가이드 29페이지는 이 운영 구조를 바탕으로 서비스 패키지와 관리 흐름을 보조 설명하는 자료로 연결했습니다.',
    imageSrc: refrigerationSystemImages.maintenance.card,
    imageAlt: '유지보수 서비스 이미지',
    tags: ['#유지보수서비스', '#원스톱대응', '#전국서비스네트워크'],
    highlights: [
      '365일 상시 고객 대응 체계',
      '3시간 내 현장 도착 알림 지원',
      '2년 무상점검 기반 예방 관리',
      '전국 서비스 네트워크 기반 신속 대응',
    ],
    metrics: [
      { label: '운영 체계', value: '고객 전담 조직·전국 네트워크' },
      { label: '현장 대응', value: '접수 후 3시간 내 출동' },
      { label: '점검 지원', value: '설치 후 2년 무상점검' },
      { label: '서비스 채널', value: '365일 상시 대응' },
    ],
    gallery: createGallery(
      '유지보수 서비스',
      {
        src: refrigerationSystemImages.maintenance.card,
        alt: '유지보수 서비스 메인 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.maintenance.customGallery.heroPreview01Alt,
        alt: '유지보수 서비스 보조 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.maintenance.customGallery.heroPreview03,
        alt: '유지보수 서비스 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      system: ['maintenance'],
      product: ['frozen-food', 'beverage'],
      operation: ['service'],
    },
  }),
  createCard('refrigeration-system', {
    id: 'built-in-frozen-showcase-reach-in',
    tabId: 'built-in-frozen-showcase',
    title: '내치형 냉동 쇼케이스',
    model: '직냉식 H890 · 인버터 겸용 · SD-W 글라스형',
    summary:
      '매장 환경에 맞춰 냉동 상품 진열과 보관을 안정적으로 지원\n하는 내치형 플러그인 냉동 쇼케이스',
    detailDescription:
      '내치형 플러그인 구조를 기반으로 냉동 상품의 진열성과 공간 효율을 함께 고려한 쇼케이스로, 평대형과 수직형 구성으로 다양한 매장 환경에 대응합니다.',
    imageSrc: refrigerationSystemImages.builtInFrozenShowcase.customGallery.preview01,
    imageAlt: '내치형 냉동 쇼케이스 이미지',
    tags: ['#내치형냉동쇼케이스', '#직냉식냉각', '#글라스형평대'],
    highlights: [
      '플러그인 기반 간편 설치 구조',
      'LED 조명·디지털 컨트롤 구성',
      '저소음·저발열 기반 에너지 절감 설계',
      '평대형·수직형으로 확장되는 매장 맞춤 라인업',
    ],
    metrics: [
      { label: '설치 구조', value: '플러그인 타입' },
      { label: '조명 구성', value: 'LED 조명' },
      { label: '운전 특성', value: '저소음·저발열' },
      { label: '적용 형태', value: '평대형·수직형' },
    ],
    gallery: createGallery(
      '내치형 냉동 쇼케이스',
      {
        src: refrigerationSystemImages.builtInFrozenShowcase.customGallery.preview01,
        alt: '내치형 냉동 쇼케이스 메인 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.builtInFrozenShowcase.customGallery.detailStage02,
        alt: '내치형 냉동 쇼케이스 보조 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.builtInFrozenShowcase.customGallery.timelineStage05,
        alt: '내치형 냉동 쇼케이스 세 번째 미리보기 이미지',
      },
    ),
    filters: {
      system: ['showcase'],
      product: ['frozen-food'],
      operation: ['layout', 'temperature'],
    },
  }),
  createCard('refrigeration-system', {
    id: 'showcase-wine-cellar-premium',
    tabId: 'showcase-wine-cellar',
    title: '프리미엄 와인셀러',
    model: '2DOOR · 3DOOR · 4DOOR 빌트인 와인셀러',
    summary:
      '국제티엔씨 특허제품을 기반으로 와인 전문매장에 적용하는 빌트인형 프리미엄 와인셀러 제품군입니다.',
    detailDescription:
      '카탈로그 표기 92~94페이지는 PDF 기준 94~96페이지에 해당하며, 이 범위를 기준으로 프리미엄 와인셀러 내용을 정리했습니다. 국제티엔씨 특허제품으로 소개되는 이 와인 쇼케이스는 와인 전문매장 판매를 전제로 하며, 밀폐형 도어와 무열선 3중 페어글라스를 통해 일정한 온도 관리와 결로 방지를 동시에 확보합니다. LED 조명은 5000K 주광색과 4000K 백색 선택이 가능하고, 상부 반사 그릴과 개폐가 쉬운 손잡이, 잠금장치를 통해 매장 진열성과 보안성을 함께 갖춥니다. 사양은 간접 냉각 방식의 냉기 강제 순환식, 자연 제상, 디지털 온도계, R-404a 냉매, 220V/60Hz, 사용온도 12~18도 기준이며 2도어 96병, 3도어 144병, 4도어 192병 적재 구성을 제공합니다. 시공사례는 일반 주문형, 4면 개방형, 빌트인 타입까지 확장됩니다.',
    imageSrc: refrigerationSystemImages.premiumWineCellar.customGallery.heroPreview01,
    imageAlt: '프리미엄 와인셀러 이미지',
    tags: ['#프리미엄와인셀러', '#와인전문매장', '#빌트인쇼케이스'],
    highlights: [
      '공간 일체감을 높이는 빌트인형 와인셀러 구조',
      '와인 보관 안정성을 높이는 무열선 3중 페어글라스 적용',
      '매장 연출 효과를 강화하는 맞춤형 LED 조명 구성',
      '보안성과 사용 편의성을 높인 잠금장치 및 도어 설계',
    ],
    metrics: [
      { label: '사용 온도', value: '12~18℃' },
      { label: '보관 수량', value: '96병·144병·192병' },
      { label: '냉각 방식', value: '간접 냉각 강제순환' },
      { label: '적용 공간', value: '와인 전문매장' },
    ],
    gallery: createGallery(
      '프리미엄 와인셀러',
      {
        src: refrigerationSystemImages.premiumWineCellar.customGallery.heroPreview01,
        alt: '프리미엄 와인셀러 메인 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.premiumWineCellar.customGallery.detailStage02,
        alt: '프리미엄 와인셀러 보조 미리보기 이미지',
      },
      {
        src: refrigerationSystemImages.premiumWineCellar.card,
        alt: '프리미엄 와인셀러 제품 미리보기 이미지',
      },
    ),
    filters: {
      system: ['cellar'],
      product: ['beverage'],
      operation: ['temperature', 'layout'],
    },
  }),
];

const co2NaturalRefrigerantCard = energyCards.find(
  (card) => card.slug === 'co2-natural-refrigerant-system-retail',
);
const guntnerUnitCoolerCard = energyCards.find(
  (card) => card.slug === 'gutner-unit-cooler-high-flow',
);
const ahuRefillFilterCard = energyCards.find((card) => card.slug === 'ahu-refill-filter-modular');
const motorDirectControlCard = energyCards.find(
  (card) => card.slug === 'motor-direct-control-system-smart-fan',
);
const openShowcaseDoorCard = energyCards.find(
  (card) => card.slug === 'open-showcase-door-retrofit',
);
const technicalDesignCard = refrigerationCards.find(
  (card) => card.slug === 'technical-design-specialized-construction-retail',
);
const maintenanceServiceCard = refrigerationCards.find(
  (card) => card.slug === 'maintenance-service-monitoring',
);
const builtInFrozenShowcaseCard = refrigerationCards.find(
  (card) => card.slug === 'built-in-frozen-showcase-reach-in',
);
const premiumWineCellarCard = refrigerationCards.find(
  (card) => card.slug === 'showcase-wine-cellar-premium',
);

const haierCarrierCard =
  builtInFrozenShowcaseCard && guntnerUnitCoolerCard
    ? createAliasedCard(guntnerUnitCoolerCard, 'energy-solution', {
        id: 'haier-carrier-showcase-lineup',
        tabId: 'haier-carrier',
        title: '하이얼 캐리어',
        model: '직냉식 H890 · 인버터 겸용 · SD-W 글라스형',
        summary:
          '매장 환경에 맞춰 냉동 상품 진열과 보관을 안정적으로 지원하는 내치형 플러그인 냉동 쇼케이스',
        detailDescription:
          '내치형 플러그인 구조를 기반으로 냉동 상품의 진열성과 공간 효율을 함께 고려한 쇼케이스로, 평대형과 수직형 구성으로 다양한 매장 환경에 대응합니다.',
        imageSrc: energyImages.haierCarrier.customGallery.preview01,
        imageAlt: '하이얼캐리어 쇼케이스 이미지',
        tags: ['#내치형냉동쇼케이스', '#직냉식냉각', '#글라스형평대'],
        highlights: [
          '플러그인 기반 간편 설치 구조',
          'LED 조명·디지털 컨트롤 구성',
          '저소음·저발열 기반 에너지 절감 설계',
          '평대형·수직형으로 확장되는 매장 맞춤 라인업',
        ],
        metrics: [
          { label: 'Plug-in', value: '내치형 플러그인 시스템' },
          { label: 'LED', value: '고내 조명 구성' },
          { label: '저소음', value: '매장 친화형 운전 구조' },
          { label: '평대·수직형', value: '매장 맞춤 진열 구성' },
        ],
        gallery: createGallery(
          '하이얼 캐리어',
          {
            src: energyImages.haierCarrier.customGallery.preview01,
            alt: '하이얼 캐리어 메인 미리보기 이미지',
          },
          {
            src: energyImages.haierCarrier.customGallery.preview08,
            alt: '하이얼 캐리어 보조 미리보기 이미지',
          },
          {
            src: energyImages.haierCarrier.customGallery.preview09,
            alt: '하이얼 캐리어 세 번째 미리보기 이미지',
          },
        ),
        filters: {
          application: ['retail', 'cold-storage'],
          equipment: ['cooler', 'door'],
          priority: ['energy-saving', 'maintenance'],
        },
      })
    : undefined;

const energyBuiltInFrozenShowcaseCard = builtInFrozenShowcaseCard
  ? createAliasedCard(builtInFrozenShowcaseCard, 'energy-solution', {
      id: 'built-in-frozen-showcase-reach-in',
      tabId: 'built-in-frozen-showcase',
    })
  : undefined;

const energyPremiumWineCellarCard = premiumWineCellarCard
  ? createAliasedCard(premiumWineCellarCard, 'energy-solution', {
      id: 'showcase-wine-cellar-premium',
      tabId: 'showcase-wine-cellar',
    })
  : undefined;

const distributionSystemCard = builtInFrozenShowcaseCard
  ? createAliasedCard(builtInFrozenShowcaseCard, 'refrigeration-system', {
      id: 'distribution-system-retail',
      tabId: 'distribution-system',
      title: '유통',
      model: 'Cold Chain Distribution',
      summary:
        '설계부터 시공, 유지관리까지 전 과정에 걸쳐\n고효율·친환경 기반의 콜드체인 솔루션을 제공하는 서비스',
      detailDescription:
        '유통 카테고리는 생산부터 판매까지 저온 환경을 유지하고, 매장 환경 맞춤 냉동·냉장 설계와 보관·유통 흐름을 고려한 창고 구축, 실시간 모니터링 기반 설비 상태 관리를 함께 제공하는 콜드체인 유통 솔루션입니다.',
      imageSrc: refrigerationSystemImages.distributionSystem.customGallery.preview01,
      imageAlt: '유통 냉동·냉장 시스템 이미지',
      tags: ['#저온유통', '#고효율', '#친환경'],
      highlights: [
        '생산부터 판매까지 저온 환경 유지',
        '매장 환경 맞춤 냉동·냉장 설계',
        '보관과 유통 흐름을 고려한 창고 구축',
        '실시간 모니터링 기반 설비 상태 관리',
      ],
      metrics: [
        { label: '적용 범위', value: '생산·보관·운송·판매' },
        { label: '핵심 구성', value: '매장 설비·저온 물류' },
        { label: '운영 포인트', value: '온도 관제·유지보수' },
        { label: '지원 범위', value: '설계·시공·운영 관리' },
      ],
      gallery: createGallery(
        '유통',
        {
          src: refrigerationSystemImages.distributionSystem.customGallery.preview01,
          alt: '유통 메인 미리보기 이미지',
        },
        {
          src: refrigerationSystemImages.distributionSystem.customGallery.preview08,
          alt: '유통 보조 미리보기 이미지',
        },
        {
          src: refrigerationSystemImages.distributionSystem.customGallery.preview09,
          alt: '유통 세 번째 미리보기 이미지',
        },
      ),
    })
  : undefined;

const agriculturalFisheriesColdStorageCard = createCard('refrigeration-system', {
  id: 'agricultural-fisheries-cold-storage-system',
  tabId: 'agricultural-fisheries-cold-storage-system',
  title: '농수축산 저장고',
  model: 'Agricultural, fishery, and livestock storage',
  summary:
    '농수축산물의 신선도와 품질 유지를 위해\n온도·습도·공기 순환을 정밀 제어하는 저온 저장 시스템',
  detailDescription:
    '농수축산 저장고는 저장 품목별 최적 온도와 습도, 공기 순환 조건을 정밀하게 제어해 신선도와 품질을 유지하는 저온 저장 시스템입니다. 소형 창고부터 대형 물류센터까지 저장 환경과 운영 규모에 맞춰 구축할 수 있습니다.',
  imageSrc: refrigerationSystemImages.agriculturalFisheriesColdStorage.customGallery.preview01,
  imageAlt: '농수축산 저장고 이미지',
  tags: ['#저온저장', '#품질유지', '#정밀제어'],
  highlights: [
    '저장 품목별 최적 온도 유지',
    '신선도 유지 및 변질 최소화',
    '저장 공간 전체 온도 편차 감소',
    '소형 창고부터 대형 물류센터까지 구축 가능',
  ],
  metrics: [
    { label: '제어 범위', value: '온도·습도·기류' },
    { label: '운영 목표', value: '신선도·품질 유지' },
    { label: '냉기 전략', value: '균일 냉기 분포' },
    { label: '대응 규모', value: '소형 창고·대형 물류센터' },
  ],
  gallery: createGallery(
    '농수축산 저장고',
    {
      src: refrigerationSystemImages.agriculturalFisheriesColdStorage.customGallery.preview01,
      alt: '농수축산 저장고 메인 미리보기 이미지',
    },
    {
      src: refrigerationSystemImages.agriculturalFisheriesColdStorage.customGallery.preview08,
      alt: '농수축산 저장고 보조 미리보기 이미지',
    },
    {
      src: refrigerationSystemImages.agriculturalFisheriesColdStorage.customGallery.preview09,
      alt: '농수축산 저장고 세 번째 미리보기 이미지',
    },
  ),
  filters: {
    system: ['cold-chain'],
    product: ['seafood'],
    operation: ['temperature', 'layout'],
  },
});

const iceMakingSystemCard = createCard('refrigeration-system', {
  id: 'ice-making-system-facility',
  tabId: 'ice-making-system',
  title: '제빙통합시스템',
  model: 'Ice Making Facility System',
  summary:
    '수산·식품 산업에 필요한 안정적인 얼음 공급을 위해 생산부터 저장, 이송까지 전 공정을 자동화한 제빙 시스템',
  detailDescription:
    '수산·식품 산업에 필요한 안정적인 얼음 공급을 위해 제빙부터 저장, 이송까지 전 공정을 자동화한 제빙 통합 시스템입니다.',
  imageSrc: refrigerationSystemImages.iceMakingSystem.customGallery.preview01,
  imageAlt: '제빙통합시스템 이미지',
  tags: ['#자동화제빙', '#대용량생산', '#균일품질'],
  highlights: [
    '생산부터 저장까지 자동화',
    '온도 편차 최소화로 일정한 품질 확보',
    '산업용 대규모 생산 설비 구축 가능',
    '연속 생산과 안정 공급 체계 구축',
  ],
  metrics: [
    { label: '자동화', value: '전 공정 자동화 설계' },
    { label: '품질', value: '안정적 얼음 품질' },
    { label: '생산 규모', value: '산업용 대규모 생산 설비' },
    { label: '공정', value: '냉각·제빙·이송·저장 연계' },
  ],
  gallery: createGallery(
    '제빙통합시스템',
    {
      src: refrigerationSystemImages.iceMakingSystem.customGallery.preview01,
      alt: '제빙통합시스템 메인 미리보기 이미지',
    },
    {
      src: refrigerationSystemImages.iceMakingSystem.customGallery.preview08,
      alt: '제빙통합시스템 보조 미리보기 이미지',
    },
    {
      src: refrigerationSystemImages.iceMakingSystem.customGallery.preview09,
      alt: '제빙통합시스템 세 번째 미리보기 이미지',
    },
  ),
  filters: {
    system: ['cold-chain'],
    product: ['seafood'],
    operation: ['temperature', 'layout'],
  },
});

const alignedEnergyCards = [
  guntnerUnitCoolerCard,
  haierCarrierCard,
  energyBuiltInFrozenShowcaseCard,
  energyPremiumWineCellarCard,
  openShowcaseDoorCard,
  motorDirectControlCard,
  ahuRefillFilterCard,
].filter((card): card is CatalogCard => Boolean(card));

const alignedCo2NaturalRefrigerantCards = [co2NaturalRefrigerantCard].filter(
  (card): card is CatalogCard => Boolean(card),
);

const alignedRefrigerationCards = [
  distributionSystemCard,
  agriculturalFisheriesColdStorageCard,
  iceMakingSystemCard,
  technicalDesignCard,
  maintenanceServiceCard,
  builtInFrozenShowcaseCard,
  premiumWineCellarCard,
].filter((card): card is CatalogCard => Boolean(card));

export const catalogCategories = {
  'co2-natural-refrigerant': {
    id: 'co2-natural-refrigerant',
    label: 'CO₂ 자연냉매',
    hero: {
      backgroundImageSrc: heroImage01,
      eyebrow: 'CO₂ 자연냉매',
      title: 'CO₂ 자연냉매',
      subtitle: '자연냉매 기반 저온 운영 솔루션',
      description:
        '자연냉매 CO₂ 기반 시스템을 기준으로 탄소중립 대응, 에너지 절감, 저소음 운전과 유지관리 편의성을 함께 확인할 수 있도록 정리했습니다.',
      spotlight: ['자연냉매 CO₂', '탄소중립 대응', '고효율 운전'],
    },
    tabs: energyTabs,
    filterGroups: energyFilterGroups,
    cards: alignedCo2NaturalRefrigerantCards,
  },
  'energy-solution': {
    id: 'energy-solution',
    label: '에너지 솔루션',
    hero: {
      backgroundImageSrc: heroImage01,
      eyebrow: '에너지 솔루션',
      title: '에너지 솔루션',
      subtitle: '에너지 절감과 장비 효율화를 함께 설계하는\n국제티엔씨 에너지 솔루션',
      description:
        'CO₂ 분리를 반영한 새 정보 구조 기준으로 군트너, 하이얼캐리어, 쇼케이스, 제어·필터 솔루션을\n한 흐름으로 탐색할 수 있도록 정리했습니다.',
      spotlight: ['친환경 냉매 전환', '에너지 절감 운전', '유지관리 최적화'],
    },
    tabs: energyTabs,
    filterGroups: energyFilterGroups,
    cards: alignedEnergyCards,
  },
  'mechanical-hvac': {
    id: 'mechanical-hvac',
    label: '공조 기계설비',
    hero: {
      backgroundImageSrc: heroImage02,
      eyebrow: '공조 기계설비',
      title: '공조 기계설비',
      subtitle: '공조와 냉난방, 위생, 자동제어를 통합\n설계·시공·운영하는 국제티엔씨 공조 기계설비',
      description:
        '브로셔 사업영역 구조를 기준으로 냉난방 설비, 공조설비, 위생설비, 자동제어를 개별 공종이 아닌 연결된 운영 체계로 재구성했습니다.\n현장 분석부터 설계·시공, 제어 연동과 유지관리까지 한 흐름으로 탐색할 수 있도록 정리했습니다.',
      spotlight: ['냉난방·공조 통합', '위생·자동제어 연동', '현장 분석 기반 운영'],
    },
    tabs: mechanicalTabs,
    filterGroups: mechanicalFilterGroups,
    cards: mechanicalCards,
  },
  'refrigeration-system': {
    id: 'refrigeration-system',
    label: '냉동·냉장 시스템',
    hero: {
      backgroundImageSrc: refrigerationHeroImage,
      eyebrow: '냉동·냉장 시스템',
      title: '냉동·냉장 시스템',
      subtitle: '유통과 저온창고, 제빙과 유지보수를 하나로 연결하는\n국제티엔씨 냉동·냉장 시스템',
      description:
        '새 정보 구조 기준으로 유통, 농수산 저온창고, 제빙, 기술설계 전문시공, 유지보수를 저온 운영 체계 안에서 다시 정리했습니다.\n시공부터 운영, 유지관리까지 연결된 흐름으로 탐색할 수 있도록 구성했습니다.',
      spotlight: ['유통 저온 운영', '저온창고·제빙', '전문 시공·유지보수'],
    },
    tabs: refrigerationTabs,
    filterGroups: refrigerationFilterGroups,
    cards: alignedRefrigerationCards,
  },
} satisfies Record<(typeof catalogCategorySlugs)[number], CatalogCategory>;

export const catalogCategoryList = Object.values(catalogCategories);

type StaticCatalogCategorySlug = keyof typeof catalogCategories;

export const isCatalogCategorySlug = (value: string): value is StaticCatalogCategorySlug => {
  return Object.prototype.hasOwnProperty.call(catalogCategories, value);
};

export const getCatalogCategory = (slug: string) => {
  return isCatalogCategorySlug(slug) ? catalogCategories[slug] : undefined;
};

export const getCatalogEntry = (categorySlug: string, itemSlug: string) => {
  const category = getCatalogCategory(categorySlug);

  if (!category) {
    return undefined;
  }

  const card = category.cards.find((entry) => entry.slug === itemSlug);

  if (!card) {
    return undefined;
  }

  return {
    card,
    category,
  };
};
